/**
===================================================
Module: frontend.src.components.TenantUsers.tsx
Description: Team member management for tenant administrators
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { ApiError, apiService } from "../services/api";
import {
  canAssignRole,
  PageQuota,
  TENANT_ROLES,
  TenantGroup,
  TenantRole,
  TenantUser,
} from "../types/tenant";
import TopBar from "./TopBar";
import ConfirmModal from "./ConfirmModal";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/TenantAdmin.css";

interface InviteDraft {
  email: string;
  first_name: string;
  last_name: string;
  tenant_role: TenantRole;
  group_ids: string[];
  page_limit: string;
}

const EMPTY_INVITE: InviteDraft = {
  email: "",
  first_name: "",
  last_name: "",
  tenant_role: "end_user",
  group_ids: [],
  page_limit: "",
};

/** Used + in progress + handed to others: the least an allowance can be. */
const floorOf = (q?: PageQuota | null): number =>
  q ? q.used + q.pending + q.allotted : 0;

const TenantUsers: React.FC = () => {
  const { t } = useTranslation("tenant");
  const { user: currentUser } = useAuth();
  const myRole = currentUser?.tenant_role ?? undefined;
  const myGroups = currentUser?.group_ids ?? [];

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [groups, setGroups] = useState<TenantGroup[]>([]);
  const [myQuota, setMyQuota] = useState<PageQuota | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [invite, setInvite] = useState<InviteDraft>(EMPTY_INVITE);
  const [editing, setEditing] = useState<TenantUser | null>(null);
  const [editPages, setEditPages] = useState("");
  const [pendingDelete, setPendingDelete] = useState<TenantUser | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const addNotification = (notification: Omit<Notification, "id">) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const reportError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.needsOtp) {
        addNotification({ type: "error", message: t("errors.otpRequired") });
        return;
      }
      if (error instanceof ApiError && error.isForbidden) {
        addNotification({
          type: "error",
          message: error.message || t("errors.forbidden"),
        });
        return;
      }
      addNotification({
        type: "error",
        message: error instanceof Error ? error.message : t("errors.generic"),
      });
    },
    [t],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [userResponse, groupResponse] = await Promise.all([
        apiService.getTenantUsers(),
        apiService.getTenantGroups(),
      ]);
      setUsers(userResponse.users);
      setGroups(groupResponse.groups);
      if (userResponse.my_quota) setMyQuota(userResponse.my_quota);
    } catch (error) {
      reportError(error);
    } finally {
      setIsLoading(false);
    }
  }, [reportError]);

  useEffect(() => {
    load();
  }, [load]);

  /** Groups the caller may place someone into. */
  const assignableGroups = groups.filter(
    (g) => myRole === "super_admin" || myGroups.includes(g.id),
  );

  const assignableRoles = TENANT_ROLES.filter((role) =>
    canAssignRole(myRole, role),
  );

  const groupName = (id: string) =>
    groups.find((g) => g.id === id)?.name ?? id;

  const canManage = (target: TenantUser) => {
    if (target.id === currentUser?.id) return false;
    if (myRole === "super_admin") return true;
    return canAssignRole(myRole, target.tenant_role ?? "end_user");
  };

  /** Whether the caller may set this member's allowance. An administrator
   *  can only adjust people funded from their own allowance, or people
   *  nobody has funded yet. */
  const canEditPages = (target: TenantUser) => {
    if (target.tenant_role === "super_admin") return false;
    if (myRole === "super_admin") return true;
    const source = target.quota?.source_id ?? null;
    const limit = target.quota?.limit ?? 0;
    return source === currentUser?.id || (source === null && limit === 0);
  };

  const fmt = (n: number | null | undefined) =>
    n == null ? t("quota.unlimited") : n.toLocaleString();

  const availableText = (q: PageQuota | null) => {
    if (!q) return "";
    return q.available == null
      ? t("quota.unlimitedToAllot")
      : t("quota.availableToAllot", { count: q.available });
  };

  const parsePages = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const n = Number(trimmed);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };

  const handleInvite = async () => {
    if (!invite.email || !invite.first_name || !invite.last_name) {
      addNotification({ type: "error", message: t("common.required") });
      return;
    }
    if (invite.group_ids.length === 0) {
      addNotification({ type: "error", message: t("users.selectGroups") });
      return;
    }
    const pages = parsePages(invite.page_limit);
    if (pages === null) {
      addNotification({ type: "error", message: t("quota.invalid") });
      return;
    }
    if (myQuota?.available != null && pages > myQuota.available) {
      addNotification({
        type: "error",
        message: t("quota.exceedsAvailable", { count: myQuota.available }),
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiService.inviteTenantUser({
        email: invite.email,
        first_name: invite.first_name,
        last_name: invite.last_name,
        tenant_role: invite.tenant_role,
        group_ids: invite.group_ids,
        page_limit: pages,
      });
      addNotification({
        type: "success",
        message: `${t("users.inviteSuccess", {
          name: response.user.full_name || response.user.email,
        })} ${t("users.emailSent", { email: response.user.email })}`,
      });
      addNotification({
        type: "info",
        persistent: true,
        message: `${t("users.temporaryPassword", {
          password: response.temporary_password,
        })} — ${t("users.temporaryPasswordHint")}`,
      });
      setIsInviting(false);
      setInvite(EMPTY_INVITE);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const openEdit = (member: TenantUser) => {
    setEditing({ ...member });
    setEditPages(String(member.quota?.limit ?? 0));
  };

  const handleUpdate = async () => {
    if (!editing) return;
    if (editing.group_ids.length === 0) {
      addNotification({ type: "error", message: t("users.selectGroups") });
      return;
    }

    const updates: Parameters<typeof apiService.updateTenantUser>[1] = {
      tenant_role: editing.tenant_role ?? "end_user",
      group_ids: editing.group_ids,
      ...(myRole === "super_admin"
        ? { account_status: editing.account_status }
        : {}),
    };

    if (canEditPages(editing) && editing.tenant_role !== "super_admin") {
      const pages = parsePages(editPages);
      if (pages === null) {
        addNotification({ type: "error", message: t("quota.invalid") });
        return;
      }
      const floor = floorOf(editing.quota);
      if (pages < floor) {
        addNotification({
          type: "error",
          message: t("quota.minimum", { count: floor }),
        });
        return;
      }
      if (pages !== (editing.quota?.limit ?? 0)) updates.page_limit = pages;
    }

    setIsSaving(true);
    try {
      const result = await apiService.updateTenantUser(editing.id, updates);
      if (result.my_quota) setMyQuota(result.my_quota);
      addNotification({ type: "success", message: t("users.updateSuccess") });
      setEditing(null);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsSaving(true);
    try {
      const result = await apiService.removeTenantUser(pendingDelete.id);
      addNotification({
        type: "success",
        message:
          result.projects_transferred > 0
            ? t("users.deleteSuccessWithDocs", {
                count: result.projects_transferred,
              })
            : t("users.deleteSuccess"),
      });
      setPendingDelete(null);
      await load();
    } catch (error) {
      reportError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleGroup = (ids: string[], groupId: string) =>
    ids.includes(groupId)
      ? ids.filter((id) => id !== groupId)
      : [...ids, groupId];

  // Cancelling an invitation nobody has used destroys nothing, so it gets a
  // lighter dialog than deleting someone who has been working.
  const deleteDialog = (target: TenantUser) => {
    const label = target.full_name || target.email || target.username;
    const documents = target.project_count ?? 0;
    const neverUsed = !target.last_login && documents === 0;
    if (neverUsed) {
      return {
        title: t("users.cancelInviteTitle"),
        message: t("users.cancelInviteConfirm", { name: label }),
        confirm: t("confirm.cancelInvite"),
      };
    }
    return {
      title: t("users.deleteTitle"),
      message:
        documents > 0
          ? t("users.deleteConfirmWithDocs", { name: label, count: documents })
          : t("users.deleteConfirm", { name: label }),
      confirm: t("confirm.delete"),
    };
  };

  const renderPages = (member: TenantUser) => {
    const q = member.quota;
    if (!q) return <span className="muted">—</span>;
    if (member.tenant_role === "super_admin") {
      return (
        <span className="pages-cell">
          {t("quota.sharesPool")}
          <span className="pages-free">
            {fmt(q.used)} / {fmt(q.limit)}
          </span>
        </span>
      );
    }
    const free = q.available ?? null;
    const tone =
      free === null ? "" : free === 0 ? "none" : free <= 20 ? "low" : "";
    return (
      <span className="pages-cell">
        {fmt(q.used)} / {fmt(q.limit)}
        <span className={`pages-free ${tone}`}>
          {q.limit === 0 && q.used === 0
            ? t("quota.notSet")
            : t("quota.freeShort", { count: free ?? 0 })}
        </span>
      </span>
    );
  };

  const groupPicker = (
    selected: string[],
    onToggle: (id: string) => void,
  ) => (
    <>
      <div className="group-picker" role="group" aria-label={t("users.groups")}>
        {assignableGroups.map((group) => (
          <label key={group.id} className="group-option">
            <input
              type="checkbox"
              checked={selected.includes(group.id)}
              onChange={() => onToggle(group.id)}
            />
            <span title={group.name}>{group.name}</span>
          </label>
        ))}
      </div>
      <p className="picker-count">
        {t("users.groupsSelected", {
          count: selected.length,
          total: assignableGroups.length,
        })}
      </p>
    </>
  );

  const col = {
    name: t("users.columns.name"),
    role: t("users.columns.role"),
    groups: t("users.columns.groups"),
    pages: t("users.columns.pages"),
    status: t("users.columns.status"),
    lastLogin: t("users.columns.lastLogin"),
    actions: t("users.columns.actions"),
  };

  return (
    <div className="tenant-container">
      <div className="tenant-content">
        <TopBar />

        <div className="tenant-header-section">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => (window.location.href = "/projects")}
            >
              {t("common:home", { defaultValue: "Home" })}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span>{t("users.title")}</span>
          </div>

          <div className="tenant-title-row">
            <div>
              <h1>{t("users.title")}</h1>
              <p className="tenant-subtitle">{t("users.subtitle")}</p>
            </div>
            <button
              className="tbtn tbtn-primary"
              onClick={() => {
                setInvite({
                  ...EMPTY_INVITE,
                  group_ids:
                    assignableGroups.length === 1 ? [assignableGroups[0].id] : [],
                });
                setIsInviting(true);
              }}
            >
              {t("users.invite")}
            </button>
          </div>
        </div>

        {myQuota && (
          <div className="quota-strip" aria-label={t("quota.title")}>
            <div className="quota-cell">
              <span className="k">
                {myQuota.scope === "pool"
                  ? t("quota.poolTitle")
                  : t("quota.yourAllowance")}
              </span>
              <span className="v">{fmt(myQuota.limit)}</span>
              {myQuota.scope !== "pool" && (
                <span className="sub">
                  {myQuota.source_name
                    ? t("quota.fromAdmin", { name: myQuota.source_name })
                    : t("quota.fromPool")}
                </span>
              )}
            </div>
            <div className="quota-cell">
              <span className="k">{t("quota.used")}</span>
              <span className="v">{fmt(myQuota.used)}</span>
            </div>
            <div className="quota-cell">
              <span className="k">{t("quota.pending")}</span>
              <span className="v">{fmt(myQuota.pending)}</span>
            </div>
            {myQuota.can_allot && (
              <div className="quota-cell">
                <span className="k">{t("quota.allotted")}</span>
                <span className="v">{fmt(myQuota.allotted)}</span>
              </div>
            )}
            <div className="quota-cell">
              <span className="k">{t("quota.available")}</span>
              <span className="v accent">{fmt(myQuota.available)}</span>
              {myQuota.can_allot && (
                <span className="sub">{t("quota.availableSub")}</span>
              )}
            </div>
            {myQuota.over_allocated > 0 && (
              <div className="quota-warning">
                {t("quota.overAllocated", { count: myQuota.over_allocated })}
              </div>
            )}
          </div>
        )}

        <div className="tenant-table-wrap">
          <table className="tenant-table">
            <thead>
              <tr>
                <th>{col.name}</th>
                <th>{col.role}</th>
                <th>{col.groups}</th>
                <th>{col.pages}</th>
                <th>{col.status}</th>
                <th>{col.lastLogin}</th>
                <th>{col.actions}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr className="row-message">
                  <td colSpan={7}>{t("users.loading")}</td>
                </tr>
              )}
              {!isLoading && users.length === 0 && (
                <tr className="row-message">
                  <td colSpan={7}>{t("users.empty")}</td>
                </tr>
              )}
              {!isLoading &&
                users.map((member) => (
                  <tr key={member.id}>
                    <td data-label={col.name}>
                      <div>
                        <span className="cell-primary">
                          {member.full_name || member.username}
                        </span>
                        <span className="cell-secondary">{member.email}</span>
                      </div>
                    </td>
                    <td data-label={col.role}>
                      <span className={`role-badge role-${member.tenant_role}`}>
                        {member.tenant_role
                          ? t(`roles.${member.tenant_role}`)
                          : "-"}
                      </span>
                    </td>
                    <td data-label={col.groups}>
                      {member.group_ids.length ? (
                        <div className="chip-list">
                          {member.group_ids.map((id) => (
                            <span key={id} className="group-chip">
                              {groupName(id)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td data-label={col.pages}>{renderPages(member)}</td>
                    <td data-label={col.status}>
                      {member.account_status
                        ? t(`users.statuses.${member.account_status}`, {
                            defaultValue: member.account_status,
                          })
                        : "-"}
                    </td>
                    <td data-label={col.lastLogin}>
                      {member.last_login
                        ? new Date(member.last_login).toLocaleString()
                        : "-"}
                    </td>
                    <td className="actions-cell" data-label={col.actions}>
                      {canManage(member) ? (
                        <div className="tbtn-group">
                          <button
                            className="tbtn tbtn-outline tbtn-sm"
                            onClick={() => openEdit(member)}
                          >
                            {t("users.edit")}
                          </button>
                          <button
                            className="tbtn tbtn-danger tbtn-sm"
                            onClick={() => setPendingDelete(member)}
                          >
                            {t("users.remove")}
                          </button>
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {isInviting && (
        <div className="modal-overlay">
          <div className="tenant-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{t("users.inviteTitle")}</h2>
            </div>

            <div className="form-section">
              <label htmlFor="inv-email">{t("users.email")}</label>
              <input
                id="inv-email"
                type="email"
                value={invite.email}
                onChange={(e) =>
                  setInvite({ ...invite, email: e.target.value })
                }
                required
              />

              <label htmlFor="inv-last">{t("users.lastName")}</label>
              <input
                id="inv-last"
                type="text"
                value={invite.last_name}
                onChange={(e) =>
                  setInvite({ ...invite, last_name: e.target.value })
                }
                required
              />

              <label htmlFor="inv-first">{t("users.firstName")}</label>
              <input
                id="inv-first"
                type="text"
                value={invite.first_name}
                onChange={(e) =>
                  setInvite({ ...invite, first_name: e.target.value })
                }
                required
              />

              <label htmlFor="inv-role">{t("users.role")}</label>
              <select
                id="inv-role"
                value={invite.tenant_role}
                onChange={(e) =>
                  setInvite({
                    ...invite,
                    tenant_role: e.target.value as TenantRole,
                  })
                }
              >
                {assignableRoles.map((role) => (
                  <option key={role} value={role}>
                    {t(`roles.${role}`)}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                {t(`roles.${invite.tenant_role}_hint`)}
              </p>

              <label>{t("users.groups")}</label>
              {groupPicker(invite.group_ids, (id) =>
                setInvite({
                  ...invite,
                  group_ids: toggleGroup(invite.group_ids, id),
                }),
              )}

              {invite.tenant_role !== "super_admin" && (
                <>
                  <label htmlFor="inv-pages">{t("quota.field")}</label>
                  <input
                    id="inv-pages"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={myQuota?.available ?? undefined}
                    step={1}
                    value={invite.page_limit}
                    onChange={(e) =>
                      setInvite({ ...invite, page_limit: e.target.value })
                    }
                    placeholder="0"
                  />
                  <p className="field-hint">
                    {t("quota.fieldHint")} {availableText(myQuota)}
                  </p>
                </>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="tbtn tbtn-outline"
                onClick={() => setIsInviting(false)}
                disabled={isSaving}
              >
                {t("common.cancel")}
              </button>
              <button
                className="tbtn tbtn-primary"
                onClick={handleInvite}
                disabled={isSaving}
              >
                {t("users.invite")}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-overlay">
          <div className="tenant-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>{t("users.editTitle")}</h2>
              <p className="muted">{editing.email}</p>
            </div>

            <div className="form-section">
              <label htmlFor="edit-role">{t("users.role")}</label>
              <select
                id="edit-role"
                value={editing.tenant_role ?? "end_user"}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    tenant_role: e.target.value as TenantRole,
                  })
                }
              >
                {assignableRoles.map((role) => (
                  <option key={role} value={role}>
                    {t(`roles.${role}`)}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                {t(`roles.${editing.tenant_role ?? "end_user"}_hint`)}
              </p>

              <label>{t("users.groups")}</label>
              {groupPicker(editing.group_ids, (id) =>
                setEditing({
                  ...editing,
                  group_ids: toggleGroup(editing.group_ids, id),
                }),
              )}

              <label htmlFor="edit-pages">{t("quota.field")}</label>
              {editing.tenant_role === "super_admin" ? (
                <p className="field-hint">{t("quota.sharesPoolLong")}</p>
              ) : (
                <>
                  <input
                    id="edit-pages"
                    type="number"
                    inputMode="numeric"
                    min={floorOf(editing.quota)}
                    step={1}
                    value={editPages}
                    disabled={!canEditPages(editing)}
                    onChange={(e) => setEditPages(e.target.value)}
                  />
                  {canEditPages(editing) ? (
                    <>
                      <p className="field-hint">
                        {t("quota.breakdown", {
                          used: editing.quota?.used ?? 0,
                          pending: editing.quota?.pending ?? 0,
                          allotted: editing.quota?.allotted ?? 0,
                        })}
                      </p>
                      <p className="field-hint">
                        {editing.quota?.source_id
                          ? editing.quota.source_id === currentUser?.id
                            ? t("quota.fromYou")
                            : t("quota.fromAdminBudget")
                          : t("quota.fromPool")}
                        {" · "}
                        {availableText(myQuota)}
                      </p>
                    </>
                  ) : (
                    <p className="field-hint warn">
                      {t("quota.managedElsewhere")}
                    </p>
                  )}
                </>
              )}

              {myRole === "super_admin" && (
                <>
                  <label htmlFor="edit-status">{t("users.status")}</label>
                  <select
                    id="edit-status"
                    value={editing.account_status ?? "active"}
                    onChange={(e) =>
                      setEditing({ ...editing, account_status: e.target.value })
                    }
                  >
                    <option value="active">{t("users.statuses.active")}</option>
                    <option value="deactivated">
                      {t("users.statuses.deactivated")}
                    </option>
                  </select>
                </>
              )}
            </div>

            <div className="modal-actions">
              <button
                className="tbtn tbtn-outline"
                onClick={() => setEditing(null)}
                disabled={isSaving}
              >
                {t("common.cancel")}
              </button>
              <button
                className="tbtn tbtn-primary"
                onClick={handleUpdate}
                disabled={isSaving}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <ConfirmModal
          title={deleteDialog(pendingDelete).title}
          message={deleteDialog(pendingDelete).message}
          confirmLabel={deleteDialog(pendingDelete).confirm}
          cancelLabel={t("confirm.keep")}
          danger
          busy={isSaving}
          onConfirm={confirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default TenantUsers;
